<template>
  <div class="pp-title-row">
    <div class="left">
      <div class="eyebrow">
        <NuxtLink
          v-if="developerName && developerSlug"
          :to="`/developers/${developerSlug}`"
          class="dev dev-link"
          >◆ {{ developerName }}</NuxtLink
        >
        <span
          v-else-if="developerName"
          class="dev"
          >◆ {{ developerName }}</span
        >
        <span
          v-if="developerName && refLabel"
          class="dot"
          >·</span
        >
        <span v-if="refLabel">{{ refLabel }}</span>
      </div>
      <h1>{{ title }}</h1>
      <div
        v-if="metaItems.length"
        class="meta"
      >
        <template
          v-for="(item, index) in metaItems"
          :key="index"
        >
          <span
            v-if="index > 0"
            class="sep"
            >·</span
          >
          <span>{{ item }}</span>
        </template>
      </div>
    </div>
    <div class="right">
      <button
        type="button"
        class="iconbtn"
        :aria-label="isFavorite ? t('property.titleRow.removeFromFavorites') : t('property.titleRow.saveToFavorites')"
        :class="{ 'is-on': isFavorite }"
        @click="$emit('toggle-favorite')"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          :fill="isFavorite ? 'currentColor' : 'none'"
          stroke="currentColor"
          stroke-width="1.8"
          aria-hidden="true"
        >
          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
        </svg>
      </button>
      <button
        v-if="showShare"
        type="button"
        class="iconbtn"
        :aria-label="t('property.titleRow.share')"
        @click="$emit('share')"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          aria-hidden="true"
        >
          <circle
            cx="18"
            cy="5"
            r="3"
          />
          <circle
            cx="6"
            cy="12"
            r="3"
          />
          <circle
            cx="18"
            cy="19"
            r="3"
          />
          <path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98" />
        </svg>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
const {
  title,
  developerName = '',
  developerSlug = '',
  refLabel = '',
  metaItems = [],
  isFavorite = false,
  showShare = true,
} = defineProps<{
  title: string;
  developerName?: string;
  developerSlug?: string;
  refLabel?: string;
  metaItems?: string[];
  isFavorite?: boolean;
  showShare?: boolean;
}>();

defineEmits<{
  (e: 'toggle-favorite'): void;
  (e: 'share'): void;
}>();

const { t } = useI18n();
</script>

<style scoped lang="scss">
.pp-title-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 22px;

  .left {
    min-width: 0;
    flex: 1;
  }

  .eyebrow {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 10px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: rgba(13, 15, 21, 0.62);
    font-weight: 700;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;

    .dev {
      color: #8b6000;
    }

    .dev-link {
      text-decoration: none;
      transition: color 0.15s ease;

      &:hover {
        color: #5d4000;
        text-decoration: underline;
        text-underline-offset: 2px;
      }
    }

    .dot {
      color: rgba(13, 15, 21, 0.2);
    }
  }

  h1 {
    font-family: 'Fraunces', 'Times New Roman', serif;
    font-weight: 600;
    font-size: 44px;
    letter-spacing: -0.025em;
    line-height: 1;
    margin: 0 0 10px;
    color: #0d0f15;
  }

  .meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 14px;
    font-size: 13px;
    color: rgba(13, 15, 21, 0.62);

    .sep {
      color: rgba(13, 15, 21, 0.2);
    }

    :deep(strong) {
      color: #0d0f15;
      font-weight: 700;
    }
  }

  .right {
    display: flex;
    gap: 8px;
    flex-shrink: 0;

    .iconbtn {
      width: 44px;
      height: 44px;
      border-radius: 12px;
      border: 1px solid rgba(13, 15, 21, 0.08);
      background: white;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      color: rgba(13, 15, 21, 0.7);
      cursor: pointer;
      transition: all 0.15s ease;

      &:hover {
        background: #f3f3f3;
        color: #0d0f15;
      }

      &.is-on {
        color: #ffb648;
        border-color: rgba(255, 182, 72, 0.5);
      }
    }
  }
}
</style>
